import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { MODULES, moduleVisible } from '../modules';
import { db } from '../db';
import type { Me } from '../session';
import { GearIcon } from '../components/GearIcon';
import { WeatherCard } from '../weather/WeatherCard';
import { buildStamp, describeBuildTime } from '../platform/build-info';

/**
 * The hub. Opening the app lands here: a tile per module. It is deliberately
 * thin — it knows each module's name and home path and nothing else, so a module
 * can change everything behind its tile without touching this screen.
 *
 * The one thing above the modules is the gear: hub-wide settings — notifications
 * and sign-out — that belong to the app rather than to any single module.
 */
/** Today, written the way the household reads it — the ledger's date line. */
function today(): string {
  const s = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function DashboardScreen() {
  const build = buildStamp();
  // The cached session, read from IndexedDB rather than asked of the server, so
  // a personal module's tile is right offline and appears without a round trip.
  const me = useLiveQuery(async () => (await db.meta.get('me'))?.value as Me | undefined, []);
  const modules = MODULES.filter((m) => m.tile !== false && moduleVisible(m, me?.user.display_name));

  return (
    <div className="app">
      <header className="dash-head">
        <div className="dash-title">
          <h1>Family Hub</h1>
          <p className="dash-date">{today()}</p>
        </div>
        <Link className="btn btn-quiet dash-settings" to="/instellingen" aria-label="Instellingen">
          <GearIcon />
        </Link>
      </header>

      <main>
        <WeatherCard />
        <div className="dash-grid">
          {modules.map((m) => (
            <Link
              key={m.id}
              to={m.path}
              className="dash-tile"
              style={{ '--tile-accent': m.accent } as React.CSSProperties}
            >
              <span className="dash-icon">{m.icon}</span>
              <span className="dash-text">
                <span className="dash-label">{m.label}</span>
              </span>
              <span className="dash-chev" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </Link>
          ))}
        </div>

        {/*
          The colophon: which build of the app this is, and when it landed.
          Below the tiles because it is read after the thing you came for —
          usually to answer "is the new bit here yet?" — and quiet enough that
          it never competes with them. It is the *bundle's* own stamp, so it
          stays true offline and never claims a deploy this phone has not
          applied (platform/build-info.ts).
        */}
        {build && (
          <footer className="dash-foot">
            <span>Laatst bijgewerkt</span>
            <time className="dash-foot-time" dateTime={build.at.toISOString()}>
              {describeBuildTime(build.at)}
            </time>
            {build.commit && <span className="dash-foot-id">{build.commit}</span>}
          </footer>
        )}
      </main>
    </div>
  );
}
